import {
  useFormShowContext,
  CloseFormShowButton,
  EditFormShowButton,
  SaveFormShowButton,
  DateInput,
  TextInput,
  TextArea,
  TogglerInput,
  FormFieldsData,
} from 'framework/form';
import { useShowContext } from 'framework/show';
import { Grid2 as Grid, Typography } from '@mui/material';
import AccessTime from '@mui/icons-material/AccessTime';
import { FormattedTextField, LinkField, TextField } from 'framework/ui/field';
import { useCallback } from 'react';
import {
  CYCLE_ACTIVITY_STATUS,
  CYCLE_DAYS_LABELS,
  ACCESS_INFO_ACTIONS,
  ACCESS_INFO_SECTIONS,
  useProjectSecurityContext,
} from 'domain/shared/newProject';
import { Tooltip } from 'framework/ui/mui-builder';

const PlanningRowItem = () => {
  const { isEditMode } = useFormShowContext();
  const { data } = useShowContext();
  const { isAccessGranted } = useProjectSecurityContext();

  const renderActionButtons = useCallback(() => {
    if (!isAccessGranted(ACCESS_INFO_SECTIONS.SDLC, ACCESS_INFO_ACTIONS.MANAGE)) {
      return null;
    }

    const canEdit =
      data?.status === CYCLE_ACTIVITY_STATUS.IN_PROGRESS ||
      data?.status === CYCLE_ACTIVITY_STATUS.NOT_STARTED ||
      (data?.status === CYCLE_ACTIVITY_STATUS.EXPIRED && !data?.planningDate);

    if (!canEdit) {
      return null;
    }

    return (
      <Grid>
        {isEditMode ? (
          <>
            <SaveFormShowButton />
            <CloseFormShowButton />
          </>
        ) : (
          <EditFormShowButton />
        )}
      </Grid>
    );
  }, [isAccessGranted, isEditMode, data?.status, data?.planningDate]);

  return (
    <Grid
      container
      size={12}
      sx={{
        borderBottom: '1px dashed',
        borderColor: 'border.default',
        pl: 7,
        pr: 2,
        direction: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}
      spacing={1}
    >
      <Grid size={isEditMode ? 10 : 3}>
        <Typography variant="body1" color="primary">
          Planning
        </Typography>
      </Grid>
      {!isEditMode && (
        <>
          <Grid
            size={3}
            gap={1}
            sx={{
              display: 'flex',
              alignItems: 'center',
              direction: 'row',
              color: 'neutral.main',
              minHeight: '40px',
            }}
          >
            {data?.planningDate && (
              <>
                <AccessTime sx={{ fontSize: '14px' }} />
                <TextField source="planningDate" value={data?.planningDate} label="" />
              </>
            )}
            {!data?.planningDate && data?.status !== CYCLE_ACTIVITY_STATUS.SKIPPED && !data?.skipReason && (
              <>
                <AccessTime sx={{ fontSize: '14px' }} />
                <FormattedTextField
                  label=""
                  value={data?.planningDay}
                  format={(str: string) => `on ${CYCLE_DAYS_LABELS[str]}`}
                />
              </>
            )}
          </Grid>
          <Grid size={4}>
            {data?.isSkipped && (
              <Tooltip title={data?.skipReason}>
                <span>
                  <Typography
                    color="error"
                    variant="body1"
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {data?.skipReason}
                  </Typography>
                </span>
              </Tooltip>
            )}
          </Grid>
        </>
      )}
      <Grid
        size={2}
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
        }}
      >
        {renderActionButtons()}
      </Grid>
      {!isEditMode && (data?.plannedScopeLink || data?.details) && (
        <>
          <Grid container size={12}>
            <Grid size={1.5}>
              <Typography variant="body1" color="textSecondary">
                Planned scope:
              </Typography>
            </Grid>
            <Grid size={9.5}>
              <LinkField linkSource="plannedScopeLink" value={data?.plannedScopeLink} />
            </Grid>
          </Grid>
          <Grid container size={12} sx={{ pb: 2 }}>
            <Grid size={1.5}>
              <Typography variant="body1" color="textSecondary">
                Details:
              </Typography>
            </Grid>
            <Grid size={9.5}>
              <TextField source="details" label="" />
            </Grid>
          </Grid>
        </>
      )}
      {isEditMode && (
        <Grid container spacing={8} size={12}>
          <Grid size={6}>
            <Grid size={5}>
              <DateInput source="planningDate" label="Planning date" />
            </Grid>
            <TextInput source="plannedScopeLink" label="Add a link to the planned scope of work" />
            <TogglerInput checkedValue="Yes" uncheckedValue="No" source="isSkipped" label="Planning was skipped" />
            <TextInput
              source="skipReason"
              placeholder="Skip reason"
              isVisible={(values: FormFieldsData) => values?.isSkipped}
            />
          </Grid>
          <Grid size={6}>
            <TextArea source="details" rows={10} />
          </Grid>
        </Grid>
      )}
    </Grid>
  );
};

export default PlanningRowItem;
